/**
 * Consent Scope Model and Types
 *
 * Implements consent-scoped data governance per the tenant-data-platform spec.
 * Supports COPPA compliance with age-gating and parental consent flows.
 */

// ============================================
// CONSENT SCOPES
// ============================================

/**
 * Data sharing scope levels - ordered from most to least restrictive
 */
export type ConsentScope =
  | 'personal_only'           // Data stays on device, never transmitted
  | 'share_with_parent'       // Parent/guardian can view
  | 'share_with_counselor'    // School counselor can view (crisis scenarios)
  | 'institution_aggregate_only'; // Only anonymized aggregates shared

/**
 * Map of consent scopes to their characteristics
 */
export const CONSENT_SCOPE_INFO: Record<ConsentScope, {
  label: string;
  description: string;
  allowsSync: boolean;
  allowsAnalytics: boolean;
  requiresParentalConsent: boolean;
}> = {
  personal_only: {
    label: 'Personal Only',
    description: 'Your data stays on this device only',
    allowsSync: false,
    allowsAnalytics: false,
    requiresParentalConsent: false,
  },
  share_with_parent: {
    label: 'Share with Parent/Guardian',
    description: 'Your parent or guardian can see your progress',
    allowsSync: true,
    allowsAnalytics: false,
    requiresParentalConsent: true,
  },
  share_with_counselor: {
    label: 'Share with Counselor',
    description: 'School counselor can view for support purposes',
    allowsSync: true,
    allowsAnalytics: false,
    requiresParentalConsent: true,
  },
  institution_aggregate_only: {
    label: 'Optional Activity Analytics',
    description: 'Share limited account-linked activity metadata for group reports',
    allowsSync: true,
    allowsAnalytics: true,
    requiresParentalConsent: false, // Optional processing still requires explicit consent
  },
};

// ============================================
// AGE VERIFICATION
// ============================================

export type AgeGroup =
  | 'under_13'    // COPPA applies - requires parental consent
  | '13_to_17'    // Minor - some restrictions apply
  | '18_plus';    // Adult - full consent capability

export interface AgeVerification {
  ageGroup: AgeGroup;
  birthYear?: number; // Optional - not stored for privacy
  verifiedAt: string; // ISO timestamp
  method: 'self_reported' | 'parent_verified' | 'institution_provided';
}

/**
 * Determine age group from birth year
 */
export function getAgeGroup(birthYear: number): AgeGroup {
  const currentYear = new Date().getFullYear();
  const age = currentYear - birthYear;

  if (age < 13) return 'under_13';
  if (age < 18) return '13_to_17';
  return '18_plus';
}

// ============================================
// PARENTAL CONSENT
// ============================================

export type ParentalConsentStatus =
  | 'not_required'     // User is 13+ or scope doesn't require it
  | 'pending'          // Awaiting parent verification
  | 'granted'          // Parent has consented
  | 'denied'           // Parent denied consent
  | 'expired';         // Consent needs renewal

export interface ParentalConsent {
  status: ParentalConsentStatus;
  parentEmail?: string; // Hashed for privacy
  requestedAt?: string; // ISO timestamp
  respondedAt?: string; // ISO timestamp
  expiresAt?: string;   // ISO timestamp - consent renewal
  verificationCode?: string; // For verification flow
}

// ============================================
// CONSENT RECORD
// ============================================

/**
 * Individual consent grant for a specific scope
 */
export interface ConsentGrant {
  scope: ConsentScope;
  granted: boolean;
  grantedAt?: string; // ISO timestamp
  revokedAt?: string; // ISO timestamp
  source: 'user' | 'parent' | 'institution_default';
}

/**
 * Complete consent state for a user
 */
export interface UserConsentState {
  // User identification
  userId: string;
  tenantId: string;

  // Age verification
  ageVerification: AgeVerification;

  // Parental consent (for under-13)
  parentalConsent: ParentalConsent;

  // Individual scope grants
  grants: ConsentGrant[];

  // Metadata
  createdAt: string;
  updatedAt: string;
  version: number; // For optimistic locking
}

// ============================================
// CONSENT POLICY
// ============================================

/**
 * Policy configuration for a tenant
 */
export interface ConsentPolicy {
  // Minimum age to use without parental consent
  minAgeNoParentalConsent: number; // Default: 13 (COPPA)

  // Default scopes for new users by age group
  defaultScopes: Record<AgeGroup, ConsentScope[]>;

  // Required scopes (user cannot disable)
  requiredScopes: ConsentScope[];

  // Scopes that are completely disabled for this tenant
  disabledScopes: ConsentScope[];

  // Consent expiration (days) - 0 means no expiration
  consentExpirationDays: number;

  // Allow users to revoke all consent (data deletion)
  allowFullRevocation: boolean;

  // Parental verification method
  parentalVerificationMethod: 'email' | 'credit_card' | 'knowledge_based' | 'institution_provided';
}

/**
 * Default consent policy (COPPA-compliant)
 */
export const DEFAULT_CONSENT_POLICY: ConsentPolicy = {
  minAgeNoParentalConsent: 13,
  defaultScopes: {
    under_13: ['personal_only'], // Most restrictive for children
    '13_to_17': ['personal_only'],
    '18_plus': ['personal_only'],
  },
  requiredScopes: [], // No forced data sharing
  disabledScopes: [],
  consentExpirationDays: 365, // Annual renewal
  allowFullRevocation: true,
  parentalVerificationMethod: 'email',
};

// ============================================
// CONSENT CHECKS
// ============================================

/**
 * Result of a consent check
 */
export interface ConsentCheckResult {
  allowed: boolean;
  reason?: 'no_consent' | 'age_restricted' | 'parental_pending' | 'scope_disabled' | 'consent_expired';
  requiredAction?: 'request_consent' | 'verify_age' | 'await_parent' | 'renew_consent';
}

/**
 * Check if a user has consent for a specific scope
 */
export function checkConsent(
  state: UserConsentState,
  scope: ConsentScope,
  policy: ConsentPolicy
): ConsentCheckResult {
  if (!state.ageVerification.verifiedAt && scope !== 'personal_only') return { allowed: false, reason: 'age_restricted', requiredAction: 'verify_age' };
  // Check if scope is disabled for this tenant
  if (policy.disabledScopes.includes(scope)) {
    return {
      allowed: false,
      reason: 'scope_disabled'
    };
  }

  // Check age restrictions
  if (state.ageVerification.ageGroup === 'under_13') {
    // Under 13 requires parental consent for any sharing
    const scopeInfo = CONSENT_SCOPE_INFO[scope];
    if (scope !== 'personal_only' || scopeInfo.requiresParentalConsent) {
      if (state.parentalConsent.status === 'pending') {
        return {
          allowed: false,
          reason: 'parental_pending',
          requiredAction: 'await_parent'
        };
      }
      if (state.parentalConsent.status !== 'granted') {
        return {
          allowed: false,
          reason: 'age_restricted',
          requiredAction: 'request_consent'
        };
      }
    }
  }

  // Find the grant for this scope
  const grant = state.grants.find(g => g.scope === scope);

  if (!grant || !grant.granted) {
    return {
      allowed: false,
      reason: 'no_consent',
      requiredAction: 'request_consent'
    };
  }

  // Check if consent has expired
  if (policy.consentExpirationDays > 0 && grant.grantedAt) {
    const grantDate = new Date(grant.grantedAt);
    const expiryDate = new Date(grantDate);
    expiryDate.setDate(expiryDate.getDate() + policy.consentExpirationDays);

    if (new Date() > expiryDate) {
      return {
        allowed: false,
        reason: 'consent_expired',
        requiredAction: 'renew_consent'
      };
    }
  }

  return { allowed: true };
}

/**
 * Check if user can grant consent for a scope (based on age/parental status)
 */
export function canGrantConsent(
  state: UserConsentState,
  scope: ConsentScope,
  policy: ConsentPolicy
): boolean {
  const scopeInfo = CONSENT_SCOPE_INFO[scope];
  if (!state.ageVerification.verifiedAt && scope !== 'personal_only') return false;

  // Under 13 can only grant consent for scopes that don't require parental consent
  if (state.ageVerification.ageGroup === 'under_13') {
    if (scope !== 'personal_only' || scopeInfo.requiresParentalConsent) {
      return state.parentalConsent.status === 'granted';
    }
  }

  // Check if scope is disabled
  if (policy.disabledScopes.includes(scope)) {
    return false;
  }

  return true;
}

// ============================================
// CONSENT EVENTS (for audit trail)
// ============================================

export type ConsentEventType =
  | 'consent_granted'
  | 'consent_revoked'
  | 'parental_consent_requested'
  | 'parental_consent_granted'
  | 'parental_consent_denied'
  | 'age_verified'
  | 'consent_expired'
  | 'full_data_deletion_requested';

export interface ConsentEvent {
  type: ConsentEventType;
  userId: string;
  tenantId: string;
  scope?: ConsentScope;
  metadata?: Record<string, unknown>;
  timestamp: string;
  ipHash?: string; // Hashed for privacy, for audit purposes
}

// ============================================
// DATA CLASSIFICATION
// ============================================

/**
 * Classification of data types and their minimum required consent scope
 */
export const DATA_CLASSIFICATION: Record<string, {
  minScope: ConsentScope;
  description: string;
  retentionDays: number; // 0 = indefinite (user-controlled)
  piiLevel: 'none' | 'low' | 'medium' | 'high';
}> = {
  // User-generated content
  journal_entry: {
    minScope: 'personal_only',
    description: 'Personal journal entries',
    retentionDays: 0,
    piiLevel: 'high',
  },
  reflection: {
    minScope: 'personal_only',
    description: 'Reflection responses',
    retentionDays: 0,
    piiLevel: 'high',
  },
  ai_conversation: {
    minScope: 'personal_only',
    description: 'AI coach conversations',
    retentionDays: 30, // Auto-delete after 30 days
    piiLevel: 'high',
  },

  // Progress data
  technique_usage: {
    minScope: 'institution_aggregate_only',
    description: 'Which techniques were used',
    retentionDays: 0,
    piiLevel: 'low',
  },
  lesson_completion: {
    minScope: 'institution_aggregate_only',
    description: 'Lesson progress tracking',
    retentionDays: 0,
    piiLevel: 'low',
  },
  achievement: {
    minScope: 'share_with_parent',
    description: 'Achievements and badges earned',
    retentionDays: 0,
    piiLevel: 'low',
  },

  // Crisis-related (special handling)
  crisis_event: {
    minScope: 'share_with_counselor', // Always escalate
    description: 'Crisis detection events',
    retentionDays: 365, // Legal retention requirement
    piiLevel: 'high',
  },
  crisis_resource_access: {
    minScope: 'share_with_counselor',
    description: 'Crisis resource usage',
    retentionDays: 365,
    piiLevel: 'medium',
  },

  // Analytics (anonymized)
  app_usage: {
    minScope: 'institution_aggregate_only',
    description: 'Anonymous usage patterns',
    retentionDays: 90,
    piiLevel: 'none',
  },
  feature_interaction: {
    minScope: 'institution_aggregate_only',
    description: 'Feature usage statistics',
    retentionDays: 90,
    piiLevel: 'none',
  },
};

/**
 * Check if data can be stored/shared based on consent
 */
export function canProcessData(
  dataType: keyof typeof DATA_CLASSIFICATION,
  userConsent: UserConsentState,
  policy: ConsentPolicy
): ConsentCheckResult {
  const classification = DATA_CLASSIFICATION[dataType];
  if (!classification) {
    return { allowed: false, reason: 'no_consent' };
  }

  return checkConsent(userConsent, classification.minScope, policy);
}

